import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Plus } from "lucide-react";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace, useMembers } from "../lib/workspaces";
import { contributes } from "../../shared/schemas/workspaces";
import { ISSUE_GROUPS, ISSUE_SORTS, type IssueSort } from "../../shared/schemas/issues";
import { DEFAULT_LIST_COLUMNS, LIST_COLUMNS, type ListColumn } from "../../shared/schemas/lists";
import type { PublicIssue } from "../../shared/types/issues";
import { useProject, useProjectByKey } from "../lib/projects";
import { useLabels } from "../lib/labels";
import { useOpenSprints } from "../lib/sprints";
import { exportHref, flattenIssuePages, prefetchIssue, useAssignToMe, useIssues, useUpdateIssue } from "../lib/issues";
import { issueSearchSchema, searchToFilters, hasActiveFilters, withoutFilters } from "../lib/issue-filters";
import { IssueFilters } from "../components/issues/IssueFilters";
import { QuickFilters } from "../components/issues/QuickFilters";
import { ProjectHeading } from "../components/projects/ProjectHeading";
import { CreateIssueDialog } from "../components/issues/CreateIssueDialog";
import { IssuePeek } from "../components/issues/IssuePeek";
import { useDeleteWithUndo } from "../components/issues/use-delete-with-undo";
import { selectionPage, useIssueShortcuts, type QuickControl } from "../components/issues/use-issue-shortcuts";
import { useShowsSelection } from "../lib/in-view";
import { openCommandBar } from "../lib/keyboard";
import { IssueTable, type TableItem } from "../components/list/IssueTable";
import { groupSummary, listCell } from "../components/list/ListCells";
import { BulkBar, ListToolbar } from "../components/list/ListControls";
import { typeName } from "../components/issues/TypeIcon";
import { ProjectKeyMoved, ProjectUnreachable } from "../components/projects/ProjectStates";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { NoResults, Unavailable } from "../components/ui/states";
import { strings } from "../lib/strings";
import { counted } from "../lib/words";
import { ApiError } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";

const words = strings.list;

/** The list's address: the filters, its order and grouping, and its columns, comma-separated. */
const listSearchSchema = issueSearchSchema.extend({
  sort: z.enum(ISSUE_SORTS).optional().catch(undefined),
  dir: z.enum(["asc", "desc"]).optional().catch(undefined),
  group: z.enum(ISSUE_GROUPS).optional().catch(undefined),
  cols: z.string().max(400).optional().catch(undefined),
});

/** Sorts that start newest first: times. */
const DESC_FIRST: readonly IssueSort[] = ["created", "updated", "resolved"];

/** This person's columns for this project, remembered in this browser: a convenience, forgotten if refused. */
function rememberedColumns(key: string): ListColumn[] | null {
  try {
    const held = window.localStorage.getItem(key);
    if (held === null) return null;
    const columns = (JSON.parse(held) as string[]).filter((c): c is ListColumn =>
      (LIST_COLUMNS as readonly string[]).includes(c),
    );
    return columns.length > 0 ? columns : null;
  } catch {
    return null;
  }
}

function ListPage() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/list" });
  const slug = workspaceSlug();
  const search = useSearch({ from: "/admin/p/$projectKey/list" });
  const navigate = useNavigate({ from: "/admin/p/$projectKey/list" });
  const view = useWorkspace(slug);
  const byKey = useProjectByKey(slug, projectKey);
  const members = useMembers(slug);
  const labels = useLabels();
  const [createOpen, setCreateOpen] = useState(false);
  const [focusedIssueId, setFocusedIssueId] = useState<string | null>(null);
  const [quickControl, setQuickControl] = useState<QuickControl | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const project = byKey.project;
  const detail = useProject(project?.id ?? "");
  const statusesById = new Map((detail.data?.statuses ?? []).map((status) => [status.id, status]));
  // A Viewer reads the list and changes nothing (BUILD_SPEC §8.2); the API refuses them as well.
  const contributor = view.data !== undefined && contributes(view.data.role) && detail.data?.canEdit === true;
  const scrum = (detail.data?.project ?? project)?.method === "scrum";
  const sprints = useOpenSprints(project?.id ?? "", scrum);
  const filters = searchToFilters(search);
  const sort: IssueSort = search.sort ?? "rank";
  const desc = search.dir === undefined ? DESC_FIRST.includes(sort) : search.dir === "desc";
  const order = {
    sort,
    ...(search.dir === undefined ? {} : { dir: search.dir }),
    ...(search.group ? { group: search.group } : {}),
  };
  const issuesQuery = useIssues(project?.id ?? "", filters, order, 100);
  const pages = issuesQuery.data?.pages ?? [];
  const issues = flattenIssuePages(issuesQuery.data);
  const update = useUpdateIssue(project?.id ?? "");
  const assign = useAssignToMe(project?.id ?? "");
  // The selection, for the command bar's changes to it (BUILD_SPEC §4.3), where the person may make them.
  useShowsSelection(contributor && project !== undefined ? project.id : null, [...selected], () =>
    setSelected(new Set()),
  );
  const columnsKey = `steer.list.columns.${project?.id ?? ""}`;
  const fromAddress = search.cols
    ?.split(",")
    .filter((c): c is ListColumn => (LIST_COLUMNS as readonly string[]).includes(c));
  const columns: ListColumn[] =
    fromAddress !== undefined && fromAddress.length > 0
      ? fromAddress
      : (rememberedColumns(columnsKey) ?? [...DEFAULT_LIST_COLUMNS]);

  const drawerIssue = search.issue ? issues.find((i) => i.key === search.issue) : undefined;
  const deleteWithUndo = useDeleteWithUndo(project?.id ?? "");
  const openIssue = (key: string) => void navigate({ search: (prev) => ({ ...prev, issue: key }) });
  const toggle = (issue: PublicIssue) => {
    const next = new Set(selected);
    if (next.has(issue.id)) next.delete(issue.id);
    else next.add(issue.id);
    setSelected(next);
    setAnchor(issue.id);
  };

  useIssueShortcuts({
    issues,
    peeking: drawerIssue !== undefined,
    onExpand: () =>
      drawerIssue !== undefined &&
      void navigate({ to: "/admin/p/$projectKey/$issueKey", params: { projectKey, issueKey: drawerIssue.key } }),
    focusedIssueId,
    setFocusedIssueId,
    onOpen: (issue) => openIssue(issue.key),
    onCreate: () => {
      if (contributor) setCreateOpen(true);
    },
    onFocusSearch: () => searchRef.current?.focus(),
    onQuick: (issue, control) => {
      // On selected issues the key changes them all, at the command bar's page for it (BUILD_SPEC §4.3).
      const page = contributor && selected.size > 0 ? selectionPage(control) : null;
      if (page !== null) openCommandBar(page);
      else {
        setQuickControl(control);
        openIssue(issue.key);
      }
    },
    onAssignMe: contributor
      ? (issue) => {
          if (selected.size === 0) assign.one(issue);
          else
            assign.many([...selected], (everyOne) => {
              if (everyOne) setSelected(new Set());
            });
        }
      : undefined,
    onSelect: toggle,
  });

  if (view.isLoading || byKey.isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-2 px-6 py-8">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if ((view.error instanceof ApiError && (view.error.status === 404 || view.error.status === 401)) || !view.data) {
    return <ToWorkspaces />;
  }
  if (!byKey.loaded)
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Unavailable what={strings.projects.list.theProjects} onRetry={byKey.refetch} page />
      </div>
    );
  if (!project || project.joinable) return <ProjectUnreachable project={project} />;
  if (project.key !== projectKey) return <ProjectKeyMoved from={projectKey} to={project.key} />;

  const memberName = (id: string | null) =>
    id ? ((members.data ?? []).find((m) => m.userId === id)?.userName ?? null) : null;
  const closeDrawer = () => {
    setQuickControl(null);
    void navigate({ search: (prev) => ({ ...prev, issue: undefined }) });
  };
  const unit =
    detail.data?.project.estimation === "points" || detail.data?.project.estimation === "hours"
      ? detail.data.project.estimation
      : null;

  // The rows: each issue in order, and where the list is grouped each group's heading before its first, with its
  // total for the whole list, not only the pages held.
  const groups = pages[0]?.groups ?? [];
  const groupKeys = pages.flatMap((page) => page.groupKeys ?? page.issues.map(() => null));
  const groupName = (key: string | null, label: string | null): string => {
    if (search.group === undefined) return "";
    if (key === null)
      return search.group in words.noneGroup
        ? words.noneGroup[search.group as keyof typeof words.noneGroup]
        : strings.issues.filters.unassigned;
    if (search.group === "priority") return strings.issues.priority[key as keyof typeof strings.issues.priority];
    if (search.group === "type") return typeName(key, label ?? key);
    return label ?? key;
  };
  const items: TableItem[] = [];
  let lastGroup: string | null | undefined;
  issues.forEach((issue, index) => {
    const group = search.group === undefined ? null : (groupKeys[index] ?? null);
    if (search.group !== undefined && group !== lastGroup) {
      const total = groups.find((g) => g.key === group);
      items.push({
        kind: "group",
        key: group,
        label: groupName(group, total?.label ?? null),
        summary: total === undefined ? "" : groupSummary(total.count, total.estimate, unit),
      });
      lastGroup = group;
    }
    items.push({ kind: "issue", issue, group });
  });
  const parents = Object.assign({}, ...pages.map((page) => page.parents)) as Record<
    string,
    { key: string; title: string }
  >;

  const onSelect = (issue: PublicIssue, event: MouseEvent | KeyboardEvent | null, range?: boolean) => {
    if (range === true && anchor !== null) {
      const ids = issues.map((i) => i.id);
      const [from, to] = [ids.indexOf(anchor), ids.indexOf(issue.id)];
      if (from >= 0 && to >= 0) {
        const next = new Set(selected);
        for (const id of ids.slice(Math.min(from, to), Math.max(from, to) + 1)) next.add(id);
        setSelected(next);
        return;
      }
    }
    if (event !== null && "key" in event) {
      // Starting a range from the keyboard: the issue joins the selection, and is its anchor.
      setSelected(new Set([...selected, issue.id]));
      setAnchor(issue.id);
      return;
    }
    toggle(issue);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <header className="mb-6 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <ProjectHeading
            project={detail.data?.project ?? project}
            projectKey={projectKey}
            current="list"
            canManage={detail.data?.canManage ?? false}
          />
          {contributor && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {strings.issues.board.newIssue}
            </Button>
          )}
        </div>
        <QuickFilters
          value={search}
          projectId={project.id}
          onChange={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }) })}
        />
        <IssueFilters
          slug={slug}
          statuses={detail.data?.statuses ?? []}
          types={detail.data?.types ?? []}
          value={search}
          onChange={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }) })}
          searchInputRef={searchRef}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ListToolbar
            columns={columns}
            onColumns={(next) => {
              try {
                window.localStorage.setItem(columnsKey, JSON.stringify(next));
              } catch {
                // Remembering is a convenience; the address holds them all the same.
              }
              void navigate({ search: (prev) => ({ ...prev, cols: next.join(",") }) });
            }}
            group={search.group}
            onGroup={(group) => void navigate({ search: (prev) => ({ ...prev, group }) })}
            exportHref={exportHref(project.id, filters, order, columns)}
          />
          {issuesQuery.data !== undefined && (
            <p role="status" className="text-sm text-[hsl(var(--muted-foreground))]">
              {counted(pages[0]?.total ?? 0, strings.backlog.issue)}
            </p>
          )}
        </div>
      </header>

      {selected.size > 0 && contributor && (
        <BulkBar
          slug={slug}
          projectId={project.id}
          selected={[...selected]}
          statuses={detail.data?.statuses ?? []}
          labels={labels.data ?? []}
          sprints={sprints.data ?? []}
          scrum={scrum}
          onClear={() => setSelected(new Set())}
        />
      )}

      {issuesQuery.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : issuesQuery.isError && issues.length === 0 ? (
        <Unavailable what={words.theList} onRetry={() => void issuesQuery.refetch()} />
      ) : issues.length === 0 && hasActiveFilters(search) ? (
        <NoResults onClear={() => void navigate({ search: (prev) => withoutFilters(prev) })} />
      ) : issues.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
          <h2 className="text-lg font-semibold">{strings.issues.board.emptyTitle}</h2>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{strings.issues.board.emptyBody}</p>
          {contributor && (
            <Button className="mt-6" onClick={() => setCreateOpen(true)}>
              {strings.issues.board.emptyCreate}
            </Button>
          )}
        </div>
      ) : (
        <IssueTable
          items={items}
          columns={columns}
          sort={sort}
          desc={desc}
          onSort={(next) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                sort: next,
                // A second click on the same column turns it round.
                dir: next === sort ? (desc ? "asc" : "desc") : undefined,
              }),
            })
          }
          selected={selected}
          onSelect={onSelect}
          onSelectAll={(all) => setSelected(all ? new Set(issues.map((i) => i.id)) : new Set())}
          focusedIssueId={focusedIssueId}
          onFocusRow={setFocusedIssueId}
          hasMore={issuesQuery.hasNextPage}
          loadingMore={issuesQuery.isFetchingNextPage}
          onLoadMore={() => void issuesQuery.fetchNextPage()}
          renderCell={(issue, column) =>
            listCell(
              {
                slug,
                statuses: statusesById,
                statusesOf: () => detail.data?.statuses ?? [],
                types: detail.data?.types ?? [],
                labels: new Map((labels.data ?? []).map((label) => [label.id, label])),
                sprints: sprints.data ?? [],
                scrum,
                parents,
                memberName,
                canEdit: contributor,
                onChange: (target, input) => update.mutate({ issueId: target.id, input }),
                onOpen: (target) => openIssue(target.key),
                onFocus: (target) => {
                  setFocusedIssueId(target.id);
                  prefetchIssue(qc, target.id);
                },
              },
              issue,
              column,
            )
          }
        />
      )}

      <CreateIssueDialog open={createOpen} onOpenChange={setCreateOpen} projectId={project.id} slug={slug} />

      {drawerIssue && (
        <IssuePeek
          issueId={drawerIssue.id}
          issueKey={drawerIssue.key}
          slug={slug}
          projectId={project.id}
          pageNeeds={760}
          onClose={closeDrawer}
          onExpand={() =>
            void navigate({ to: "/admin/p/$projectKey/$issueKey", params: { projectKey, issueKey: drawerIssue.key } })
          }
          onRequestDelete={() => deleteWithUndo.requestDelete(drawerIssue.id, { onStart: closeDrawer })}
          issueKeyForId={(id) => issues.find((i) => i.id === id)?.key ?? null}
          autoOpen={quickControl ?? undefined}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/list")({
  validateSearch: listSearchSchema,
  component: ListPage,
});
